/**
 * A learner's place in a FlashBook, following them between devices.
 *
 * A book runs to a hundred pages and is read over days, which means the same book
 * is picked up on a phone on the bus and a laptop at the kitchen table. The device
 * store (`#/lib/flashbookProgressStore`) is the half that has to answer *now*: it
 * is synchronous, it works offline, and it is what the cover reads on its first
 * frame. This is the half that carries the place across — one record per book, in
 * the learner's own repo, written as they read.
 *
 * Three decisions shape it.
 *
 *  - **One record per book, at a key both devices compute.** The key is built from
 *    the book's own AT-URI (see {@link progressRecordKey}), so any install lands on
 *    the same record: a single `getRecord` answers *where am I in this book*, with
 *    no list to page through and no index to keep in step. `putRecord` replaces or
 *    creates, so a device that has never written one can still write it without
 *    asking first.
 *
 *  - **The device copy wins when the two disagree.** The place on the machine in
 *    the learner's hand is the place the learner last *acted*, and a remote copy
 *    that pulled it backwards — to page 12, because the phone had not been picked
 *    up since Tuesday — would be worse than a stale one. So a device that has a
 *    read open keeps its own copy, and the merge says so rather than leaving it to
 *    the caller; a device with nothing open takes the remote's up, which is the
 *    case the feature exists for, because the second device is by definition the
 *    one that has not been used yet.
 *
 *    What the losing copy loses is a *forward* move: a read taken further on the
 *    laptop while the phone sits at page 12 will not drag the phone forward. That
 *    is the deliberate half of the trade — see {@link mergeRemoteRead}.
 *
 *  - **The register is not in the record.** A finished read is already written to
 *    the AppView by `createFlashbookPlay`, and the list of ended reads is a fact
 *    about reading on *this* device (see `flashbookProgress`). What travels is the
 *    open read: the place, the answers already given on the way there, and the
 *    clock — because a resume that restored a position without the answers would
 *    claim the learner had done nothing on the pages they had already read.
 *
 * The record is read defensively, and not out of habit: it is a network shape
 * written by a *different install* of the app, which may be an older one or a
 * newer one, and a resumer that trusted it would hand the player a page result it
 * cannot render. A record that is not about the book asked for, or that holds
 * nothing a page can be rebuilt from, is treated as no record at all.
 */
import {
  adoptRead,
  emptyProgress,
  type FlashBookOpenRead,
  type FlashBookReadProgress,
  type FlashBookReadSession,
  missedDigest,
  pageCosts,
  pointsOnOffer,
  resultsSummary,
} from '#/lib/flashbookProgress'
import {type FlashBookProgressIndex} from '#/lib/flashbookProgressStore'
import {
  type FlashBookBlockResult,
  type FlashBookPage,
  type FlashBookPageResult,
} from '#/types/flashbook'

/**
 * The collection a reading place lives in.
 *
 * A record type of the app's own in the learner's repo, like
 * `app.bsky.feed.flashbook` itself. Deliberately *not* an AppView method: the
 * place is private to the learner and nothing else ever needs to read it, so
 * going through the PDS's own record API is both the shortest path and the only
 * one that works on a deployment whose AppView has not been taught about it.
 */
export const PROGRESS_COLLECTION = 'app.bsky.feed.flashbookProgress'

/** The record, as it sits in the repo. */
export type FlashBookProgressRecord = {
  $type: typeof PROGRESS_COLLECTION
  /** The book this is the place *in*, so a record can be checked against the ask. */
  bookUri: string
  /**
   * The title as it was when the place was last written.
   *
   * Carried for the same reason the device store carries it: a record found on a
   * device that has never seen the book should be able to name what it is offering
   * to continue, before the book itself has arrived.
   */
  bookTitle: string
  updatedAt: string
  /** The read in progress — where it got to, and what was answered on the way. */
  open: FlashBookOpenRead
}

/**
 * The key one book's place is kept at.
 *
 * `read-<the book's own rkey>-<a fingerprint of its AT-URI>`, and both halves earn
 * their place. The first half makes the learner's repo legible — a person looking
 * at their own records can see which book a place belongs to. The second is what
 * makes the key *unique*: an rkey is only unique within one repo, so two books by
 * two authors can legitimately carry the same one, and without the fingerprint
 * those two books would share a reading place and each would overwrite the other's.
 *
 * The fingerprint covers the whole URI rather than just its author, so the key does
 * not depend on the readable half surviving unchanged: sanitising an unusual rkey
 * can only alter the *label*, never the identity. A collision now needs two
 * different books agreeing on 64 bits of FNV-1a, which is not something a learner
 * can arrange by accident.
 */
export function progressRecordKey(bookUri: string): string {
  const parts = bookUri.match(/^at:\/\/([^/]+)\/[^/]+\/(.+)$/)
  const label = sanitiseRkey(parts?.[2] ?? bookUri)
  return `read-${label}-${fingerprint(bookUri)}`
}

/**
 * The rkey charset, minus the punctuation that only adds noise.
 *
 * AT Protocol allows `[A-Za-z0-9._~:-]`; everything else — and `:` and the dots,
 * which are legal but read as separators in a key that already has some — is
 * folded into a dash, and the result is capped so an absurd rkey cannot produce an
 * absurd key.
 */
function sanitiseRkey(value: string): string {
  const cleaned = value
    .replace(/[^A-Za-z0-9._~-]+/g, '-')
    .replace(/^[.-]+|[.-]+$/g, '')
  return cleaned.slice(0, 64) || 'book'
}

/**
 * A short, stable fingerprint of a string.
 *
 * FNV-1a twice, with different starting offsets, concatenated in base 36: one pass
 * is 32 bits, which is plenty to tell two authors apart in one learner's library
 * but only ~4 billion values to collide in, and the two passes cost four lines and
 * make the key collision-free for any library that will ever exist. Both passes
 * are pure arithmetic on the string, so two devices — and two platforms — agree.
 */
function fingerprint(value: string): string {
  const pass = (seed: number) => {
    let hash = seed
    for (let index = 0; index < value.length; index++) {
      hash ^= value.charCodeAt(index)
      hash = Math.imul(hash, 0x01000193) >>> 0
    }
    return hash.toString(36).padStart(7, '0')
  }
  return pass(0x811c9dc5) + pass(0x9e3779b9)
}

/**
 * The record for a read in progress, or `null` when there is nothing open.
 *
 * A book whose read has ended has no place to carry, and the caller writes the
 * *absence* by clearing the record rather than by writing one that says "nothing"
 * — a record that exists is a record the next device has to parse and dismiss.
 */
export function progressRecordFor(
  progress: FlashBookReadProgress,
): FlashBookProgressRecord | null {
  const open = progress.open
  if (!open) return null
  return {
    $type: PROGRESS_COLLECTION,
    bookUri: progress.bookUri,
    bookTitle: progress.bookTitle,
    updatedAt: progress.updatedAt,
    open: {
      id: open.id,
      startedAt: open.startedAt,
      pageIndex: open.pageIndex,
      pageCount: open.pageCount,
      pageResults: open.pageResults,
      timeMs: open.timeMs,
    },
  }
}

/**
 * The open read a record holds — or `null` when it holds nothing usable.
 *
 * The book check is not politeness: `getRecord` answers whatever lives at the key
 * it was given, and a record is also readable by its author on another build of
 * this app. A place that names a different book is a place in a different book, and
 * adopting it would put the learner in the middle of a page of something else.
 */
export function openReadFromRecord(
  value: unknown,
  bookUri: string,
): FlashBookOpenRead | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  if (record.bookUri !== bookUri) return null
  const open = record.open
  if (!open || typeof open !== 'object') return null
  const read = open as Record<string, unknown>
  if (typeof read.id !== 'string' || !read.id) return null
  return {
    id: read.id,
    startedAt: typeof read.startedAt === 'string' ? read.startedAt : '',
    pageIndex: wholeNumber(read.pageIndex),
    // A book with no pages is not a book, and a zero count here would clamp every
    // position to nothing (see `saveReadPosition`'s guard, which is the same idea).
    pageCount: Math.max(1, wholeNumber(read.pageCount)),
    pageResults: pageResultsFrom(read.pageResults),
    timeMs: wholeNumber(read.timeMs),
  }
}

/** Page results a record holds, keeping only the ones a page can be rebuilt from. */
function pageResultsFrom(value: unknown): FlashBookPageResult[] {
  if (!Array.isArray(value)) return []
  const results: FlashBookPageResult[] = []
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue
    const page = entry as Record<string, unknown>
    // A result without the page it belongs to cannot be placed, and a resume that
    // guessed would attribute a learner's answers to the wrong page.
    if (typeof page.pageId !== 'string' || !page.pageId) continue
    results.push({
      pageId: page.pageId,
      timeSpentMs: wholeNumber(page.timeSpentMs),
      blocks: blockResultsFrom(page.blocks),
    })
  }
  return results
}

/**
 * Block results, with the numbers the report divides by made safe.
 *
 * Everything else on a block result is carried through untouched — `userAnswer`
 * above all, which is what the report's "your answer" line reads. Only the two
 * fields the scoring arithmetic depends on are normalised, because a `points` of
 * `undefined` would not fail: it would silently score the page as zero.
 */
function blockResultsFrom(value: unknown): FlashBookBlockResult[] {
  if (!Array.isArray(value)) return []
  const blocks: FlashBookBlockResult[] = []
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue
    const block = entry as FlashBookBlockResult
    if (typeof block.blockId !== 'string' || !block.blockId) continue
    blocks.push({
      ...block,
      type: typeof block.type === 'string' ? block.type : '',
      timeSpentMs: wholeNumber(block.timeSpentMs),
      points: Number.isFinite(block.points) ? block.points : 0,
    })
  }
  return blocks
}

/** A non-negative whole number, or 0 — a NaN clock must not reach the register. */
function wholeNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.trunc(value)
    : 0
}

/** What hearing from the PDS decided. */
export type ProgressMerge = {
  /** The progress this device should hold. Unchanged unless a read was adopted. */
  progress: FlashBookReadProgress | null
  /** True when the remote read was taken up, because this device had none open. */
  adopted: boolean
  /**
   * True when the device's own copy is the one the record should hold.
   *
   * If the two agree there is nothing to say; if the device's copy won, the record
   * is still the other device's answer and will stay that way — so the winner is
   * what gets written back. That is what makes "the device wins" observable
   * somewhere other than this device: the learner's *next* device reads it.
   */
  push: boolean
}

/**
 * Reconcile what the device has with what the learner's repo says.
 *
 * The rules, in the order they are tried:
 *
 *  1. **No remote read** — nothing to hear; the device keeps its own copy, and
 *     offers it to the record (the other device has either never been used or has
 *     never read this book).
 *  2. **The device has a read open** — it wins, whatever the remote says. This is
 *     the rule the feature was asked for, and it is the one that can be *felt*: a
 *     device that is halfway through stays halfway through, however long the other
 *     one has been reading.
 *  3. **The device has nothing open** — it takes the remote's read up. Either it
 *     has never read this book, or it has only ever *finished* it, and in both
 *     cases there is no place of its own to lose.
 *
 * The register — the ended reads and the totals — is never taken from the remote,
 * even in the third case: it is this device's own history (see the module note),
 * and the record does not carry one to take.
 */
export function mergeRemoteRead({
  local,
  remote,
  bookUri,
  bookTitle,
  now = new Date().toISOString(),
}: {
  local: FlashBookReadProgress | null
  remote: FlashBookOpenRead | null
  bookUri: string
  /** The book's title as it is *now*, which is fresher than the record's. */
  bookTitle: string
  now?: string
}): ProgressMerge {
  if (!remote) {
    return {progress: local, adopted: false, push: !!local?.open}
  }
  if (local?.open) {
    return {
      progress: local,
      adopted: false,
      push: !sameOpenRead(local.open, remote),
    }
  }
  const base = local ?? emptyProgress(bookUri, bookTitle, now)
  return {
    progress: {
      ...base,
      bookTitle: bookTitle || base.bookTitle,
      updatedAt: now,
      open: remote,
    },
    adopted: true,
    // Nothing to write: the record already says exactly this, and a device that
    // has just taken a place up has nothing to add to it.
    push: false,
  }
}

/**
 * Whether two copies are the same read at the same place.
 *
 * Deliberately coarse about the answers: the question this answers is *should the
 * record be rewritten*, and a spurious write costs one small request while a
 * missed one leaves the other device behind. So a differing page count, clock or
 * set of answered pages all count as a disagreement, and only the identity and the
 * position have to match for the two to be called the same.
 */
export function sameOpenRead(
  a: FlashBookOpenRead,
  b: FlashBookOpenRead,
): boolean {
  return (
    a.id === b.id &&
    a.pageIndex === b.pageIndex &&
    a.pageCount === b.pageCount &&
    a.pageResults.length === b.pageResults.length &&
    a.timeMs === b.timeMs
  )
}

/**
 * The place a record holds, as a reading progress — what a card draws.
 *
 * The record carries a title and a clock; the register of ended reads belongs to the
 * device and is deliberately not invented here. Only the open read is adopted.
 */
export function progressFromRecord(
  value: unknown,
  bookUri: string,
): FlashBookReadProgress | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const open = openReadFromRecord(value, bookUri)
  if (!open) return null
  const updatedAt =
    typeof record.updatedAt === 'string'
      ? record.updatedAt
      : new Date().toISOString()
  const bookTitle = typeof record.bookTitle === 'string' ? record.bookTitle : ''
  return {
    ...emptyProgress(bookUri, bookTitle, updatedAt),
    updatedAt,
    open,
  }
}

/**
 * The device's places, with the learner's other devices' places filled in behind them.
 *
 * The device's own open read wins whatever the repo says; a book it has nothing open
 * in takes the record's place; and nothing is ever removed.
 */
export function mergeRemotePlaces({
  local,
  remote,
}: {
  local: FlashBookProgressIndex | null | undefined
  remote: Record<string, FlashBookReadProgress> | null | undefined
}): FlashBookProgressIndex {
  const merged: FlashBookProgressIndex = {...local}
  for (const [bookUri, progress] of Object.entries(remote ?? {})) {
    if (!progress?.open) continue
    if (local?.[bookUri]?.open) continue
    merged[bookUri] = progress
  }
  return merged
}

/**
 * One of the learner's finished reads, as the AppView remembers it.
 *
 * Returns `null` for any play that cannot be reliably identified or dated.
 */
export function sessionFromPlay(
  play: unknown,
  {pages, bookUri}: {pages: readonly FlashBookPage[]; bookUri: string},
): FlashBookReadSession | null {
  if (!play || typeof play !== 'object') return null
  const value = play as Record<string, unknown>
  if (value.flashbookUri !== bookUri) return null
  if (typeof value.id !== 'string' || !value.id) return null
  if (typeof value.createdAt !== 'string') return null
  const endedAt = value.createdAt
  if (!Number.isFinite(Date.parse(endedAt))) return null

  const pageResults = pageResultsFrom(value.pageResults)
  const timeMs = wholeNumber(value.timeMs)
  return {
    id: value.id,
    playId: value.id,
    startedAt: new Date(Date.parse(endedAt) - timeMs).toISOString(),
    endedAt,
    completed: true,
    pageReached: Math.max(0, pages.length - 1),
    pageCount: Math.max(1, pages.length),
    answered: resultsSummary(pageResults).answered,
    points: wholeNumber(value.score),
    maxPoints: pointsOnOffer(pages),
    timeMs,
    trouble: pageCosts(pages, pageResults),
    ...(Array.isArray(value.pageResults)
      ? missedDigest(pages, pageResults)
      : {}),
  }
}

/**
 * Rebuild the register from the plays the learner's own account remembers.
 *
 * Plays already held (matched by id or playId) are skipped; everything else is
 * filed as a read taken on another device. Local reads are never removed.
 */
export function mergeRemotePlays({
  progress,
  plays,
  pages,
  bookUri,
  bookTitle,
  now = new Date().toISOString(),
}: {
  progress: FlashBookReadProgress | null
  plays: readonly unknown[]
  pages: readonly FlashBookPage[]
  bookUri: string
  bookTitle: string
  now?: string
}): {progress: FlashBookReadProgress | null; adopted: number} {
  const known = new Set<string>()
  for (const session of progress?.sessions ?? []) {
    known.add(session.id)
    if (session.playId) known.add(session.playId)
  }

  let next = progress
  let adopted = 0
  for (const play of plays) {
    const session = sessionFromPlay(play, {pages, bookUri})
    if (!session || known.has(session.id)) continue
    known.add(session.id)
    next = adoptRead(next ?? emptyProgress(bookUri, bookTitle, now), session, {
      now,
    })
    adopted += 1
  }

  return {progress: next, adopted}
}
